import { useRef, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
} from '@mui/material';
import CelebrationIcon from '@mui/icons-material/Celebration';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { heritageColors } from '../../../../heritageTheme.ts';

const List = ({ initialData = { simchas: [] }, title = 'שמחות בקהילה', showTitle = true, scale = 1 }) => {
  const contentRef = useRef(null);
  const containerRef = useRef(null);
  const originalContentRef = useRef(null);
  const [needsScroll, setNeedsScroll] = useState(false);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const simchas = (initialData.simchas || []).filter(
    s => !s.expiryDate || new Date(s.expiryDate) > now
  );

  useEffect(() => {
    const checkScroll = () => {
      if (containerRef.current) {
        // חישוב גובה התוכן המקורי בלבד (לא כולל שכפולים)
        const containerHeight = containerRef.current.clientHeight;
        // מחשבים את הגובה של פריט אחד כפול מספר הפריטים
        const itemHeight = simchas.length > 0 ? containerRef.current.querySelector('[data-simcha-item]')?.offsetHeight || 0 : 0;
        const gap = 12; // הרווח בין פריטים
        const contentHeight = simchas.length * (itemHeight + gap);

        // רק אם התוכן גדול משמעותית מהקונטיינר - נפעיל אנימציה
        const threshold = containerHeight * 0.1; // 10% מגובה הקונטיינר
        setNeedsScroll(contentHeight > containerHeight + threshold);
      }
    };

    // מחכים שה-DOM יתעדכן לפני הבדיקה
    const timer = setTimeout(checkScroll, 100);

    const resizeObserver = new ResizeObserver(checkScroll);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
    };
  }, [simchas]);

  const boldLabel = (type, label, boldWords) => {
    if (type !== 'other') return <strong>{label}</strong>;
    if (!boldWords?.length) return label;
    return label.split(/(\s+)/).map((w, i) =>
      boldWords.includes(w.trim()) && w.trim() ? <strong key={i}>{w}</strong> : w
    );
  };

  const getSimchaConfig = (type, hasCelebrant, customType) => {
    const configs = {
      birth_boy:       { icon: FavoriteIcon,    label: 'הולדת הבן' },
      birth_girl:      { icon: FavoriteIcon,    label: 'הולדת הבת' },
      grandchild_boy:  { icon: FavoriteIcon,    label: 'הולדת נכד' },
      grandchild_girl: { icon: FavoriteIcon,    label: 'הולדת נכדה' },
      engagement_boy:  { icon: FavoriteIcon,    label: hasCelebrant ? 'אירוסי' : 'אירוסי הבן' },
      engagement_girl: { icon: FavoriteIcon,    label: hasCelebrant ? 'אירוסי' : 'אירוסי הבת' },
      wedding_boy:     { icon: CelebrationIcon, label: hasCelebrant ? 'חתונת' : 'חתונת הבן' },
      wedding_girl:    { icon: CelebrationIcon, label: hasCelebrant ? 'חתונת' : 'חתונת הבת' },
      bar_mitzvah:     { icon: CelebrationIcon, label: 'בר מצווה' },
      other:           { icon: CelebrationIcon, label: customType || 'שמחה' },
    };
    return configs[type] || { icon: CelebrationIcon, label: customType || 'שמחה' };
  };

  return (
    <Box sx={{ p: `${12 / scale}px`, direction: 'rtl', height: '100%', width: '100%', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
      {/* Header */}
      {showTitle && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: `${8 / scale}px`, mb: `${12 / scale}px` }}>
          <CelebrationIcon sx={{ color: heritageColors.gold, fontSize: `${20 / scale}px` }} />
          <Typography variant="h6" sx={{ color: heritageColors.navy, fontWeight: 700, fontSize: `${20 / scale}px` }}>
            {title}
          </Typography>
        </Box>
      )}

      {/* Celebrations */}
      <Box ref={containerRef} sx={{ overflow: 'hidden', flex: 1, position: 'relative', width: '100%' }}>
        {simchas.length === 0 ? (
          <Typography sx={{ textAlign: 'center', color: heritageColors.slate, py: `${12 / scale}px`, fontSize: `${14 / scale}px` }}>
            אין שמחות כרגע
          </Typography>
        ) : (
          <>
            {needsScroll && (
              <style>
                {`
                    @keyframes scrollSimchas {
                      0% { transform: translateY(0); }
                      100% { transform: translateY(calc(-100% / 3)); }
                    }
                  `}
              </style>
            )}
            <div ref={contentRef} style={{
              animation: needsScroll ? 'scrollSimchas 30s linear infinite' : 'none'
            }}>
              <div ref={originalContentRef} style={{ display: 'flex', flexDirection: 'column', gap: `${12 / scale}px` }}>
                {simchas.map((simcha) => {
                  const config = getSimchaConfig(simcha.type, !!simcha.celebrant, simcha.customType);
                  const IconComponent = config.icon;
                  return (
                    <Paper
                      key={simcha.id}
                      data-simcha-item
                      elevation={0}
                      sx={{
                        p: `${8 / scale}px`,
                        borderRadius: `${20 / scale}px`,
                        bgcolor: heritageColors.goldLight + '15',
                        border: `${0.5 / scale}px solid ${heritageColors.gold}30`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: `${10 / scale}px` }}>
                        <IconComponent sx={{ color: heritageColors.gold, fontSize: `${16 / scale}px`, mt: `${2 / scale}px` }} />
                        <Box>
                          <Typography sx={{
                            color: heritageColors.navy,
                            fontWeight: 600,
                            fontSize: `${15 / scale}px`,
                            mb: `${4 / scale}px`,
                          }}>
                            {simcha.name}
                          </Typography>
                          <Typography sx={{
                            color: heritageColors.slate,
                            fontSize: `${12 / scale}px`,
                          }}>
                            ל{boldLabel(simcha.type, config.label, simcha.customTypeBold)}{simcha.celebrant ? ` ${simcha.celebrant}` : ''}
                          </Typography>
                          {simcha.additionalText && (
                            <Typography sx={{
                              color: heritageColors.navy,
                              fontSize: `${12 / scale}px`,
                              mt: `${4 / scale}px`,
                              fontStyle: 'italic',
                            }}>
                              {simcha.additionalText}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
                {needsScroll && (
                  <>
                    {simchas.map((simcha) => {
                      const config = getSimchaConfig(simcha.type, !!simcha.celebrant, simcha.customType);
                      const IconComponent = config.icon;
                      return (
                        <Paper
                          key={`dup1-${simcha.id}`}
                          elevation={0}
                          sx={{
                            p: `${8 / scale}px`,
                            borderRadius: `${20 / scale}px`,
                            bgcolor: heritageColors.goldLight + '15',
                            border: `${0.5 / scale}px solid ${heritageColors.gold}30`,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: `${10 / scale}px` }}>
                            <IconComponent sx={{ color: heritageColors.gold, fontSize: `${16 / scale}px`, mt: `${2 / scale}px` }} />
                            <Box>
                              <Typography sx={{
                                color: heritageColors.navy,
                                fontWeight: 600,
                                fontSize: `${15 / scale}px`,
                                mb: `${4 / scale}px`,
                              }}>
                                {simcha.name}
                              </Typography>
                              <Typography sx={{
                                color: heritageColors.slate,
                                fontSize: `${12 / scale}px`,
                              }}>
                                ל{boldLabel(simcha.type, config.label, simcha.customTypeBold)}{simcha.celebrant ? ` ${simcha.celebrant}` : ''}
                              </Typography>
                              {simcha.additionalText && (
                                <Typography sx={{
                                  color: heritageColors.navy,
                                  fontSize: `${12 / scale}px`,
                                  mt: `${4 / scale}px`,
                                  fontStyle: 'italic',
                                }}>
                                  {simcha.additionalText}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Paper>
                      );
                    })}
                    {simchas.map((simcha) => {
                      const config = getSimchaConfig(simcha.type, !!simcha.celebrant, simcha.customType);
                      const IconComponent = config.icon;
                      return (
                        <Paper
                          key={`dup2-${simcha.id}`}
                          elevation={0}
                          sx={{
                            p: `${8 / scale}px`,
                            borderRadius: `${20 / scale}px`,
                            bgcolor: heritageColors.goldLight + '15',
                            border: `${0.5 / scale}px solid ${heritageColors.gold}30`,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: `${10 / scale}px` }}>
                            <IconComponent sx={{ color: heritageColors.gold, fontSize: `${16 / scale}px`, mt: `${2 / scale}px` }} />
                            <Box>
                              <Typography sx={{
                                color: heritageColors.navy,
                                fontWeight: 600,
                                fontSize: `${15 / scale}px`,
                                mb: `${4 / scale}px`,
                              }}>
                                {simcha.name}
                              </Typography>
                              <Typography sx={{
                                color: heritageColors.slate,
                                fontSize: `${12 / scale}px`,
                              }}>
                                ל{boldLabel(simcha.type, config.label, simcha.customTypeBold)}{simcha.celebrant ? ` ${simcha.celebrant}` : ''}
                              </Typography>
                              {simcha.additionalText && (
                                <Typography sx={{
                                  color: heritageColors.navy,
                                  fontSize: `${12 / scale}px`,
                                  mt: `${4 / scale}px`,
                                  fontStyle: 'italic',
                                }}>
                                  {simcha.additionalText}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Paper>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </Box>
    </Box>
  );
};

export default List;


